import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import { TerminalTopBar } from "@/components/pos/terminal/TerminalTopBar";
import { ProductSearchPane } from "@/components/pos/terminal/ProductSearchPane";
import { CartPane } from "@/components/pos/terminal/CartPane";
import { MobileViewTabs, MobileCartBar } from "@/components/pos/terminal/MobileBars";
import { CartChatSheet } from "@/components/live/CartChatSheet";
import { SendToPrepDialog } from "@/components/pos/SendToPrepDialog";
import { useCartLive, useTitleFlash, silenceBell } from "@/hooks/useCartLive";
import { playNotifySound, playReadySound } from "@/lib/sound";
import { applyPosTheme, getPosTheme, setPosTheme } from "@/lib/posTheme";
import { useIsDesktop } from "@/hooks/useMediaQuery";

/** Tam ekran Hızlı Satış terminali — masaüstü: sol ürün arama + sağ sepet yan yana; mobil: tek pano (Ürün Ara ↔ Sepet) + sabit sepet çubuğu. */
export function PosTerminal({ open, onPark, onNewCart, pc, user, isAdmin, restricted, workerLocked, search, cart }) {
  const [theme, setTheme] = useState(getPosTheme);
  const [view, setView] = useState("search");
  const [chatOpen, setChatOpen] = useState(false);
  const [prepCart, setPrepCart] = useState(null);
  const isDesktop = useIsDesktop();
  const cl = useCartLive(pc.cartId, { userId: user?.id, onIncoming: (m) => {
    playNotifySound();
    silenceBell("pos_mesaj");
    toast.message(`${m.side === "prep" ? "Depodan mesaj" : "Sepet mesajı"} · ${m.from_name}`, { description: [m.product_name, m.text || "Fotoğraf gönderdi"].filter(Boolean).join(" — "), duration: 12000,
      action: { label: "Aç", onClick: () => setChatOpen(true) } });
  } });
  useTitleFlash("pos-terminal", chatOpen ? 0 : cl.unread);
  // Depo bir kalemi hazırlayınca: satır 6 sn yeşil yanıp söner + "HAZIR ✓" + yumuşak çan + bildirim
  const [readyKeys, setReadyKeys] = useState(() => new Set());
  const prevPrep = useRef(null);
  useEffect(() => {
    const p = cl.prep;
    const prev = prevPrep.current;
    prevPrep.current = p ? { id: p.id, status: p.status, ready: new Set((p.items || []).filter((i) => i.prepared && !i.removed).map((i) => i.key)) } : null;
    if (!p || !prev || prev.id !== p.id) return;
    const fresh = (p.items || []).filter((i) => i.prepared && !i.removed && !prev.ready.has(i.key));
    const allDone = p.status === "tamamlandi" && prev.status !== "tamamlandi";
    if (!fresh.length && !allDone) return;
    playReadySound();
    if (fresh.length) {
      const keys = fresh.map((i) => i.key);
      setReadyKeys((s) => new Set([...s, ...keys]));
      setTimeout(() => setReadyKeys((s) => { const n = new Set(s); keys.forEach((k) => n.delete(k)); return n; }), 6000);
    }
    if (allDone) toast.success("Depo tüm listeyi hazırladı ✓", { description: `${p.assigned_name || "Depo"} · ${p.quote_no || ""}`, duration: 7000 });
    else toast.success(fresh.length === 1 ? `Depo hazırladı: ${fresh[0].name}` : `Depo ${fresh.length} kalemi hazırladı`, { description: fresh.length > 1 ? fresh.map((i) => i.name).join(" · ") : `${p.prepared}/${p.total} kalem hazır`, duration: 5000 });
  }, [cl.prep]);
  useEffect(() => { applyPosTheme(theme, open); return () => applyPosTheme(theme, false); }, [theme, open]);
  useEffect(() => { if (open) setView("search"); }, [open]);
  useEffect(() => { if (!pc.cartId) setChatOpen(false); }, [pc.cartId]);
  const toggleTheme = () => setTheme((t) => { const n = t === "dark" ? "light" : "dark"; setPosTheme(n); return n; });
  const count = cart.cart.length, grand = cart.totals.grand;
  const showSearch = isDesktop || view === "search", showCart = isDesktop || view === "cart";
  const openSendPrep = async () => {
    const data = await pc.flush();   // bekleyen değişiklikler sunucuya akar → liste sepetin GÜNCEL haliyle açılır
    const id = data?.id || pc.cartId;
    if (!id) return toast.error("Sepet henüz kaydedilmedi — bir ürün ekleyip tekrar deneyin");
    setPrepCart({ id, items: pc.items, title: pc.title });
  };
  // Sepet mesajlaşması hazırlık listesi olmadan da açık (sepet henüz kaydedilmediyse önce kaydedilir)
  const openChat = async () => {
    if (!pc.cartId) {
      const data = await pc.flush();
      if (!data?.id && !pc.cartId) return toast.error("Sepet henüz kaydedilmedi — bir ürün ekleyip tekrar deneyin");
    }
    setChatOpen(true);
  };
  const prepCtl = pc.cartId || count ? { prep: cl.prep, canSend: count > 0, onSend: openSendPrep, onOpen: () => setChatOpen(true) } : null;
  const products = (cl.prep?.items || cart.cart.map((l) => ({ key: l.product_id, name: l.name }))).filter((p) => !p.removed).map((p) => ({ key: p.key, name: p.name }));
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) onPark(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { e.preventDefault(); onPark(); }} aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col bg-background text-foreground outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 duration-200"
          data-testid="quick-sale-dialog" data-pos-view={isDesktop ? "desktop" : view}>
          <DialogPrimitive.Title className="sr-only">Hızlı Satış Terminali</DialogPrimitive.Title>
          <TerminalTopBar pc={pc} user={user} cart={cart.cart} mode={cart.mode} modes={cart.modes} restricted={restricted} onMode={cart.changeMode}
            theme={theme} onTheme={toggleTheme} onPark={onPark} onNewCart={onNewCart} cartCount={count}
            chat={pc.cartId || count ? { unread: cl.unread, onOpen: openChat } : null} prep={prepCtl} />
          {!isDesktop && <MobileViewTabs view={view} onView={setView} count={count} grand={grand} restricted={restricted} unread={cl.unread} />}
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
            {showSearch && (
              <div className="lg:col-span-5 min-h-0 overflow-hidden lg:border-r border-border" data-testid="pos-search-col">
                <ProductSearchPane s={search} mode={cart.mode} restricted={restricted} isAdmin={isAdmin} title={cart.title} />
              </div>
            )}
            {showCart && (
              <div className="lg:col-span-7 min-h-0 overflow-hidden" data-testid="pos-cart-col">
                <CartPane c={cart} pc={pc} isAdmin={isAdmin} restricted={restricted} workerLocked={workerLocked} onGoSearch={isDesktop ? undefined : () => setView("search")} prepItems={cl.prep?.items} readyKeys={readyKeys} />
              </div>
            )}
          </div>
          {!isDesktop && view === "search" && <MobileCartBar count={count} grand={grand} restricted={restricted} onOpen={() => setView("cart")} />}
          <CartChatSheet open={chatOpen} onOpenChange={setChatOpen} side="cart" cl={cl} userId={user?.id}
            title={pc.title || "Perakende Müşteri"} subtitle={cl.prep ? `${cl.prep.assigned_name} · ${cl.prep.quote_no}` : pc.owner} products={products} />
          <SendToPrepDialog cart={prepCart} onClose={() => setPrepCart(null)} onSent={() => { setPrepCart(null); cl.reload(); }} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
